import { calculateNativeGrossValue, calculateRemainingNativeQuantity } from '@einhaus/project314-shared';
import { formatNativeGrossValueEquation, formatQuantity, getQuantityLabel } from '@/utils/nativeQuantity';
import React from 'react';
import Alert from '@/components/UI/Alert';
import Button from '@/components/UI/Button';
import Modal from '@/components/UI/Modal';
import FormNumberInput from '@/components/UI/FormNumberInput';
import DeleteButton from '@/components/UI/DeleteButton';
import type { ModifiableOrder } from './modifyOrderPrice';

interface ModifyOrderModalProps {
    isOpen: boolean;
    order: ModifiableOrder;
    price: number | null;
    contractMultiplier: number | null;
    isLoading: boolean;
    error: string | null;
    onPriceChange: (price: number | null) => void;
    onCancelOrder: () => void;
    onModifyOrder: () => void;
    onClose: () => void;
}

const ModifyOrderModal: React.FC<ModifyOrderModalProps> = ({
    isOpen,
    order,
    price,
    contractMultiplier,
    isLoading,
    error,
    onPriceChange,
    onCancelOrder,
    onModifyOrder,
    onClose
}) => {
    const isOption = order.derivativeType === 'Option';
    const isDynamicPriceEntry = order.priceExecution != null;
    const remainingQuantity = calculateRemainingNativeQuantity({
        instrument: order,
        quantity: order.quantity,
        quantityFilled: order.quantityFilled
    });
    const quantityLabel = `${getQuantityLabel(order)} Remaining`;
    const validQuantity = remainingQuantity > 0;
    const validPrice = price !== null && Number.isFinite(price) && price > 0;
    const estimatedValue =
        validQuantity && validPrice && contractMultiplier !== null
            ? calculateNativeGrossValue({ instrument: order, quantity: remainingQuantity, price, contractMultiplier })
            : null;

    return (
        <Modal
            closeOnEsc={!isLoading}
            closeOnOverlayClick={!isLoading}
            hideCloseButton={isLoading}
            isOpen={isOpen}
            size="lg"
            title={isDynamicPriceEntry ? 'Dynamic Price Entry' : 'Modify Pending Order'}
            onClose={onClose}
        >
            <div className="p-3">
                {error && <Alert variant="danger">{error}</Alert>}

                <div className="mb-3">
                    <h5 className="text-light font-medium mb-2">Order Details</h5>
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse bg-[#1a1a1a] border border-zinc-700">
                            <thead>
                                <tr>
                                    <th className="border border-zinc-700 p-2">Order Type</th>
                                    <th className="border border-zinc-700 p-2">Position ID</th>
                                    <th className="border border-zinc-700 p-2">Order ID</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className="border border-zinc-700 p-2">{order.type}</td>
                                    <td className="border border-zinc-700 p-2">{order.positionId}</td>
                                    <td className="border border-zinc-700 p-2">{order.id}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="mb-3">
                    <span className="block text-sm font-medium text-zinc-200 mb-1">{quantityLabel}</span>
                    <div className="w-full bg-[#1a1a1a] border border-zinc-600 rounded px-3 py-2 text-white">
                        {formatQuantity(remainingQuantity)}
                    </div>
                    <p className="text-sm text-zinc-400 mt-1">
                        {isDynamicPriceEntry
                            ? 'The strategy target is frozen. You can cancel this order, but its price cannot be changed.'
                            : 'Only the limit price can be changed.'}
                    </p>
                </div>

                <div className="mb-3">
                    <FormNumberInput
                        noMargin
                        disabled={isLoading || isDynamicPriceEntry}
                        id="modifyPrice"
                        helpText={isOption ? 'The quoted option premium per underlying unit, before the contract multiplier.' : undefined}
                        label={isOption ? 'Limit Premium Quote' : 'Limit Price'}
                        min={0.01}
                        step={0.01}
                        value={price}
                        onValueChange={onPriceChange}
                    />
                </div>

                <div className="mb-3">
                    <span className="block text-sm font-medium text-zinc-200 mb-1">Estimated Gross Value</span>
                    <div className="w-full bg-[#1a1a1a] border border-zinc-600 rounded px-3 py-2 text-white">
                        {estimatedValue === null ? '—' : `$${estimatedValue.toLocaleString('en-US')}`}
                    </div>
                    {isOption && estimatedValue !== null && contractMultiplier !== null && price !== null && (
                        <p className="mt-1 text-sm text-zinc-400">
                            {formatNativeGrossValueEquation({
                                instrument: order,
                                quantity: remainingQuantity,
                                quotedPrice: price,
                                contractMultiplier
                            })}
                        </p>
                    )}
                </div>

                <div className="flex justify-between">
                    <DeleteButton
                        disabled={isLoading}
                        showText={isDynamicPriceEntry}
                        text={isLoading ? 'Canceling...' : 'Cancel Order'}
                        onClick={onCancelOrder}
                    />

                    <div>
                        <Button className="mr-2" disabled={isLoading} variant="secondary" onClick={onClose}>
                            Close
                        </Button>
                        {!isDynamicPriceEntry && (
                            <Button disabled={isLoading || !validQuantity || !validPrice} variant="primary" onClick={onModifyOrder}>
                                {isLoading ? 'Updating...' : 'Update Order'}
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default ModifyOrderModal;
